import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export interface ProjectCarouselItem {
  id: string;
  title: string;
  description: string;
  image: string;
  link: string;
  tags?: string[];
  scrollDuration?: number; // Duration in seconds for image scroll animation
  enableScroll?: boolean; // Whether to enable scroll animation for this project
}

interface ProjectCarouselProps {
  projects: ProjectCarouselItem[];
  autoPlayInterval?: number;
  showDots?: boolean;
  showArrows?: boolean;
  className?: string;
  defaultScrollDuration?: number; // Default scroll duration for all projects (in seconds)
  enableScrollByDefault?: boolean; // Enable scroll animation by default
}

const ProjectCarousel = ({
  projects,
  autoPlayInterval = 5000,
  showDots = true,
  showArrows = true,
  className = "",
  defaultScrollDuration = 8,
  enableScrollByDefault = true
}: ProjectCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || projects.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === projects.length - 1 ? 0 : prevIndex + 1
      );
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [currentIndex, isAutoPlaying, projects.length, autoPlayInterval]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? projects.length - 1 : currentIndex - 1;
    goToSlide(newIndex);
  };

  const goToNext = () => {
    const newIndex = currentIndex === projects.length - 1 ? 0 : currentIndex + 1;
    goToSlide(newIndex);
  };

  const handleProjectClick = (link: string) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  if (projects.length === 0) {
    return null;
  }

  const currentProject = projects[currentIndex];
  const shouldScroll = currentProject.enableScroll ?? enableScrollByDefault;
  const scrollDuration = currentProject.scrollDuration ?? defaultScrollDuration;

  return (
    <div className={`relative w-full ${className}`}>
      {/* Main carousel container */}
      <div
        className="relative h-64 md:h-80 overflow-hidden rounded-xl bg-dark-lighter border border-accent/20"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="absolute inset-0 cursor-pointer"
            onClick={() => handleProjectClick(currentProject.link)}
          >
            {/* Background image with scroll animation */}
            <div className="absolute inset-0 overflow-hidden">
              <motion.img
                src={currentProject.image}
                alt={currentProject.title}
                className="absolute top-0 left-0 w-full h-auto min-h-full object-cover object-top"
                initial={{ y: 0 }}
                animate={shouldScroll ? { y: '-30%' } : { y: 0 }}
                transition={{
                  duration: scrollDuration,
                  ease: "linear",
                  repeat: shouldScroll ? Infinity : 0,
                  repeatType: "reverse"
                }}
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 h-full flex items-center">
              <div className="px-8 py-6 max-w-2xl">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  {currentProject.title}
                </h3>
                <p className="text-gray-300 mb-4 text-sm md:text-base leading-relaxed">
                  {currentProject.description}
                </p>

                {/* Tags */}
                {currentProject.tags && currentProject.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {currentProject.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full border border-primary/30"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Click indicator */}
                <div className="flex items-center gap-2 text-primary text-sm font-medium">
                  <span>View Project</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation arrows */}
        {showArrows && projects.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-200 backdrop-blur-sm"
              aria-label="Previous project"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-200 backdrop-blur-sm"
              aria-label="Next project"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Dots indicator */}
      {showDots && projects.length > 1 && (
        <div className="flex justify-center mt-6 space-x-2">
          {projects.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${
                index === currentIndex
                  ? 'bg-primary scale-125'
                  : 'bg-gray-600 hover:bg-gray-500'
              }`}
              aria-label={`Go to project ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Progress bar (optional visual indicator) */}
      {isAutoPlaying && projects.length > 1 && (
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-800">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: autoPlayInterval / 1000, ease: "linear" }}
            key={currentIndex}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectCarousel;